import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { createInfiniteQueryOptions, LightweightQueryOptions } from '@/lib/api-calls';
import { DEFAULT_PAGE_SIZE } from '@/lib/pagination';

/**
 * Hook to use an infinite query with a regular query function.
 * This adapts a paginated query function for infinite scrolling.
 *
 * @example
 * ```tsx
 * const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteScrollQuery(
 *   (pageIndex, pageSize) => getStudents({ pageIndex, pageSize, filters: { query: 'test' } }),
 *   (result) => result.students,
 * );
 * ```
 */
export function useInfiniteScrollQuery<Result extends { total: number; pageIndex: number; pageSize: number }>(
  requestQuery: (pageIndex: number, pageSize: number) => LightweightQueryOptions<Promise<Result>>,
  getDataArray: (result: Result) => unknown[],
  pageSize: number = DEFAULT_PAGE_SIZE,
) {
  const infiniteQueryOptions = useMemo(
    () => createInfiniteQueryOptions<Result>(requestQuery, getDataArray, pageSize),
    [requestQuery, getDataArray, pageSize],
  );

  return useInfiniteQuery(infiniteQueryOptions);
}
